import { useRef, useState } from "react";
import { Wand2, RefreshCw, Upload, Download, CheckSquare } from "lucide-react";
import { useT } from "@/i18n";
import { AI_TEXT, QUICK_EDITS, REVIEW } from "@/components/ailab/content";
import useImageGen, { fileToDataUrl } from "@/components/ailab/useImageGen";

const field = "w-full bg-white border-4 border-sky-100 rounded-2xl px-4 py-2.5 font-semibold focus:outline-none focus:border-sky-300";

export default function Studio({ prompt, setPrompt, allowUpload = false, hidePrompt = false, testId = "studio" }) {
  const { lang } = useT();
  const tx = AI_TEXT[lang];
  const { generate, loading } = useImageGen(tx.error, lang);
  const [versions, setVersions] = useState([]);
  const [current, setCurrent] = useState(null);
  const [edit, setEdit] = useState("");
  const fileRef = useRef(null);

  const add = (img) => { if (img) { setVersions((v) => [...v, img].slice(-6)); setCurrent(img); } };
  const create = async () => add(await generate(prompt));
  const improve = async (text) => { if (current && text.trim()) { add(await generate(text, current)); setEdit(""); } };
  const onUpload = async (e) => { const f = e.target.files?.[0]; if (f) add(await fileToDataUrl(f)); e.target.value = ""; };

  return (
    <div className="space-y-4" data-testid={testId}>
      {!hidePrompt && (
        <div>
          <p className="font-bold text-blue-950 mb-1">{tx.prompt}</p>
          <textarea value={prompt} onChange={(e) => setPrompt(e.target.value.slice(0, 600))} rows={3} className={field} data-testid={`${testId}-prompt`} />
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={create} disabled={loading || prompt.trim().length < 3} className="glossy-btn bg-yellow-300 text-blue-900 font-fredoka font-bold text-lg px-6 py-2.5 rounded-full inline-flex items-center gap-2 disabled:opacity-60" data-testid={`${testId}-create-btn`}>
          {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Wand2 className="w-5 h-5" />} {loading ? tx.creating : tx.create}
        </button>
        {allowUpload && (
          <>
            <button type="button" onClick={() => fileRef.current?.click()} disabled={loading} className="bg-white rounded-full px-5 py-2.5 font-bold text-blue-900 shadow inline-flex items-center gap-2" data-testid={`${testId}-upload-btn`}>
              <Upload className="w-4 h-4" /> {tx.upload}
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onUpload} data-testid={`${testId}-file-input`} />
          </>
        )}
      </div>
      {current && (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="relative">
            <img src={current} alt="" className={`w-full rounded-3xl border-4 border-white shadow-lg ${loading ? "opacity-50" : ""}`} data-testid={`${testId}-result`} />
            <a href={current} download="lumi-spark-ia.jpg" className="absolute top-3 right-3 bg-white/90 rounded-full p-2 shadow" aria-label={tx.download} data-testid={`${testId}-download`}><Download className="w-5 h-5 text-blue-900" /></a>
          </div>
          <div className="bg-white rounded-3xl p-4 shadow-md space-y-3">
            <p className="font-fredoka text-xl font-bold text-blue-950">{tx.editTitle}</p>
            <ul className="space-y-1">
              {REVIEW[lang].map((r) => <li key={r} className="flex gap-2 text-sm font-semibold text-blue-900"><CheckSquare className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />{r}</li>)}
            </ul>
            <input value={edit} onChange={(e) => setEdit(e.target.value.slice(0, 300))} placeholder={tx.editPh} className={field} data-testid={`${testId}-edit-input`} />
            <div className="flex flex-wrap gap-1.5">
              {QUICK_EDITS[lang].map((q) => <button key={q} type="button" onClick={() => setEdit(q)} className="bg-violet-100 hover:bg-violet-200 rounded-full px-3 py-1 text-xs font-bold text-violet-900">{q}</button>)}
            </div>
            <button type="button" onClick={() => improve(edit)} disabled={loading || !edit.trim()} className="bg-violet-600 text-white rounded-full px-5 py-2 font-bold inline-flex items-center gap-2 disabled:opacity-60" data-testid={`${testId}-improve-btn`}>
              <Wand2 className="w-4 h-4" /> {tx.editBtn}
            </button>
          </div>
        </div>
      )}
      {versions.length > 1 && (
        <div>
          <p className="font-bold text-blue-950 text-sm">{tx.versions}</p>
          <div className="mt-2 flex gap-2 overflow-x-auto">
            {versions.map((v, i) => (
              <button key={i} type="button" onClick={() => setCurrent(v)} className={`shrink-0 rounded-xl overflow-hidden border-4 ${v === current ? "border-yellow-300" : "border-white"}`} data-testid={`${testId}-version-${i}`}>
                <img src={v} alt="" className="w-16 h-16 object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export { field as studioField };
